import type {
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  MouseEvent,
  ReactElement,
  ReactNode,
} from 'react'
import { forwardRef } from 'react'

import type { WhiteLabel } from '@skytech/types'
import AlertOctagonIcon from 'mdi-react/AlertOctagonIcon'
import { twJoin, twMerge } from 'tailwind-merge'

import { LoaderCircle } from './LoaderCircle'

type ButtonSize = '2xs' | 'extraSmall' | 'small' | 'medium' | 'regular' | 'large'
type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'danger'

type ButtonProps = {
  id?: string
  children?: ReactNode
  className?: string
  selected?: boolean
  size?: ButtonSize
  type?: DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>['type']
  variant?: ButtonVariant
  Icon?: ReactElement<{ className?: string }>
  whiteLabel?: WhiteLabel
  disabled?: boolean
  isLoading?: boolean
  isIconRight?: boolean
  onClick?: (event: MouseEvent<HTMLElement>) => void
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      children,
      size = 'regular',
      variant = 'secondary',
      Icon,
      whiteLabel: _whiteLabel,
      disabled,
      selected,
      isLoading,
      isIconRight,
      onClick,
      type = 'button',
      ...restOfProps
    },
    ref
  ): ReactElement => {
    // todo: replace with the org from url when available
    const storedWhiteLabel = localStorage.getItem('org') as WhiteLabel
    const buttonIcon = variant === 'danger' && !Icon ? <AlertOctagonIcon /> : Icon
    const withIconVariant = buttonIcon ? (isIconRight ? 'rightIcon' : 'leftIcon') : 'noIcon'
    const whiteLabel = _whiteLabel || storedWhiteLabel || 'control'

    return (
      <button
        ref={ref}
        type={type}
        className={twMerge(
          'relative inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-lg border-none text-base transition ease-out',
          'mx-2 my-1.5',
          buttonSizeClasses[size],
          'px-5 py-0',
          whiteLabelVariantColorClasses[whiteLabel]?.[variant],
          isLoading && 'pointer-events-none',
          ['primary', 'secondary'].includes(variant) &&
            'disabled:pointer-events-none disabled:bg-zinc-50 disabled:text-zinc-400 disabled:shadow-none',
          variant === 'tertiary' && 'bg-transparent hover:underline',
          selected && 'shadow-basic bg-white',
          className
        )}
        disabled={disabled || isLoading}
        onClick={onClick}
        {...restOfProps}
      >
        {isLoading && <LoaderCircle size="small" />}

        <div className={twJoin('flex items-center', isLoading && 'opacity-0')}>
          {withIconVariant === 'leftIcon' && (
            <span
              className={twMerge(
                'flex',
                iconAndSizeBasedIconStyles[size]?.[withIconVariant],
                buttonIcon?.props.className
              )}
            >
              {buttonIcon}
            </span>
          )}

          {children}

          {withIconVariant === 'rightIcon' && (
            <span
              className={twMerge(
                'flex',
                iconAndSizeBasedIconStyles[size]?.[withIconVariant],
                buttonIcon?.props.className
              )}
            >
              {buttonIcon}
            </span>
          )}
        </div>
      </button>
    )
  }
)

const buttonSizeClasses = {
  '2xs': 'h-5 text-sm',
  extraSmall: 'h-7',
  small: 'h-7',
  regular: 'h-[42px]',
  medium: 'h-9',
  large: 'h-14 ',
}

const iconAndSizeBasedIconStyles: Partial<
  Record<ButtonSize, Record<'leftIcon' | 'rightIcon', string>>
> = {
  '2xs': {
    leftIcon: '[&>*]:w-3 mr-0.5 ml-0',
    rightIcon: '[&>*]:w-3 ml-0.5 mr-0',
  },
  extraSmall: {
    leftIcon: '[&>*]:w-5 mr-0.5 ml-0',
    rightIcon: '[&>*]:w-5 ml-0.5 mr-0',
  },
  small: {
    leftIcon: '[&>*]:w-[1.4rem] mr-0.5 ml-0',
    rightIcon: '[&>*]:w-[1.4rem] ml-0.5 mr-0',
  },
  regular: {
    leftIcon: '[&>*]:w-[1.4rem] mr-1 ml-0',
    rightIcon: '[&>*]:w-[1.4rem] ml-1 mr-0',
  },
  medium: {
    leftIcon: '[&>*]:w-[1.4rem] mr-0.5 ml-0',
    rightIcon: '[&>*]:w-[1.4rem] ml-0.5 mr-0',
  },
  large: {
    leftIcon: '[&>*]:w-8 mr-0.5 ml-0',
    rightIcon: '[&>*]:w-8 ml-0.5 mr-0',
  },
}

const whiteLabelVariantColorClasses: Partial<Record<WhiteLabel, Record<ButtonVariant, string>>> = {
  control: {
    primary:
      'bg-skytech-orange text-white hover:brightness-[0.9] active:brightness-[0.9] focus:brightness-[0.9]',
    secondary:
      'text-zinc-700 bg-zinc-100 hover:bg-white hover:shadow-basic focus:bg-white focus:text-skytech-orange focus:shadow-basic active:bg-white active:shadow-basic active:text-skytech-orange',
    tertiary: 'active:text-skytech-orange focus:text-skytech-orange text-zinc-700',
    danger:
      'bg-red-600 font-semibold text-white hover:brightness-[0.9] focus:brightness-[0.9] active:brightness-[0.9] disabled:pointer-events-none disabled:bg-zinc-50 disabled:text-zinc-400 disabled:shadow-none',
  },
  skytech: {
    primary:
      'bg-skytech-orange text-white hover:brightness-[0.9] active:brightness-[0.9] focus:brightness-[0.9]',
    secondary:
      'text-zinc-700 bg-zinc-100 hover:bg-white hover:shadow-basic focus:bg-white focus:text-skytech-orange focus:shadow-basic active:bg-white active:shadow-basic active:text-skytech-orange',
    tertiary: 'active:text-skytech-orange focus:text-skytech-orange text-zinc-700',
    danger:
      'bg-red-600 font-semibold text-white hover:brightness-[0.9] focus:brightness-[0.9] active:brightness-[0.9] disabled:pointer-events-none disabled:bg-zinc-50 disabled:text-zinc-400 disabled:shadow-none',
  },
  tcm: {
    primary:
      'bg-skytech-orange text-white hover:brightness-[0.9] active:brightness-[0.9] focus:brightness-[0.9]',
    secondary:
      'text-zinc-700 bg-zinc-100 hover:bg-white hover:shadow-basic focus:bg-white focus:text-skytech-orange focus:shadow-basic active:bg-white active:shadow-basic active:text-skytech-orange',
    tertiary: 'active:text-skytech-orange focus:text-skytech-orange text-zinc-700',
    danger:
      'bg-red-600 font-semibold text-white hover:brightness-[0.9] focus:brightness-[0.9] active:brightness-[0.9] disabled:pointer-events-none disabled:bg-zinc-50 disabled:text-zinc-400 disabled:shadow-none',
  },
  arp: {
    primary: 'bg-arp hover:brightness-[94] active:brightness-[94] focus:brightness-[94] text-white',
    secondary:
      'text-zinc-700 active:text-arp focus:text-arp hover:bg-white hover:shadow-basic focus:bg-white focus:text-arp focus:shadow-basic active:bg-white active:shadow-basic active:text-arp',
    tertiary: 'active:text-arp focus:text-arp text-zinc-700',
    danger:
      'bg-red-600 font-semibold text-white hover:brightness-[0.9] focus:brightness-[0.9] active:brightness-[0.9] disabled:pointer-events-none disabled:bg-zinc-50 disabled:text-zinc-400 disabled:shadow-none',
  },
  mobit: {
    primary:
      'bg-mobit text-white brightness-[1.7] hover:brightness-[1.2] focus:brightness-[1.2] active:brightness-[1.2]',
    secondary:
      'text-zinc-700 active:text-mobit focus:text-mobit hover:bg-white hover:shadow-basic focus:bg-white focus:text-mobit focus:shadow-basic active:bg-white active:shadow-basic active:text-mobit',

    tertiary: 'active:text-mobit focus:text-mobit text-zinc-700',
    danger:
      'bg-red-600 font-semibold text-white hover:brightness-[0.9] focus:brightness-[0.9] active:brightness-[0.9] disabled:pointer-events-none disabled:bg-zinc-50 disabled:text-zinc-400 disabled:shadow-none',
  },
  evry: {
    secondary:
      'text-zinc-700 active:text-evry focus:text-evry hover:bg-white hover:shadow-basic focus:bg-white focus:text-evry focus:shadow-basic active:bg-white active:shadow-basic active:text-evry',
    primary:
      'text-white bg-tietoevry hover:brightness[0.95] active:brightness[0.95] focus:brightness[0.95]',
    tertiary: 'active:color:evry focus:color:evry text-zinc-700',
    danger:
      'bg-red-600 font-semibold text-white hover:brightness-[0.9] focus:brightness-[0.9] active:brightness-[0.9] disabled:pointer-events-none disabled:bg-zinc-50 disabled:text-zinc-400 disabled:shadow-none',
  },
  tietoevry: {
    secondary:
      'text-zinc-700 active:text-evry focus:text-evry hover:bg-white hover:shadow-basic focus:bg-white focus:text-evry focus:shadow-basic active:bg-white active:shadow-basic active:text-evry',
    primary: 'bg-tietoevry  hover:brightness[0.95] active:brightness[0.95] focus:brightness[0.95]',
    tertiary: 'active:color:evry focus:color:evry text-zinc-700',
    danger:
      'bg-red-600 font-semibold text-white hover:brightness-[0.9] focus:brightness-[0.9] active:brightness-[0.9] disabled:pointer-events-none disabled:bg-zinc-50 disabled:text-zinc-400 disabled:shadow-none',
  },
  bechtle: {
    secondary:
      'text-zinc-700 active:text-bechtle focus:text-bechtle hover:bg-white hover:shadow-basic focus:bg-white focus:text-bechtle focus:shadow-basic active:bg-white active:shadow-basic active:text-bechtle',
    primary:
      'bg-bechtle text-white  hover:brightness[0.95] active:brightness[0.95] focus:brightness[0.95]',
    tertiary: 'active:text-bechtle-text focus:text-bechtle-text text-zinc-700',
    danger:
      'bg-red-600 font-semibold text-white hover:brightness-[0.9] focus:brightness-[0.9] active:brightness-[0.9] disabled:pointer-events-none disabled:bg-zinc-50 disabled:text-zinc-400 disabled:shadow-none',
  },
  csi: {
    secondary:
      'text-zinc-700 active:text-csi focus:text-csi hover:bg-white hover:shadow-basic focus:bg-white focus:text-csi focus:shadow-basic active:bg-white active:shadow-basic active:text-csi',
    primary:
      'bg-csi  hover:brightness[0.95] active:brightness[0.95] focus:brightness[0.95] text-white',
    tertiary: 'focus:text-csi active:text-csi text-zinc-700',
    danger:
      'bg-red-600 font-semibold text-white hover:brightness-[0.9] focus:brightness-[0.9] active:brightness-[0.9] disabled:pointer-events-none disabled:bg-zinc-50 disabled:text-zinc-400 disabled:shadow-none',
  },
  telia: {
    secondary:
      'text-zinc-700 active:text-telia focus:text-telia hover:bg-white hover:shadow-basic focus:bg-white focus:text-telia focus:shadow-basic active:bg-white active:shadow-basic active:text-telia',
    primary:
      'bg-telia text-white font-telia hover:brightness[0.95] active:brightness[0.95] focus:brightness[0.95]',
    tertiary: 'font-telia active:text-telia focus:text-telia text-zinc-700',
    danger:
      'bg-telia-red font-telia font-semibold text-white hover:brightness-[0.9] focus:brightness-[0.9] active:brightness-[0.9] disabled:pointer-events-none disabled:bg-zinc-50 disabled:text-zinc-400 disabled:shadow-none',
  },
  elkjop: {
    secondary:
      'text-zinc-700 active:text-elkjop focus:text-elkjop hover:bg-white hover:shadow-basic focus:bg-white focus:text-elkjop focus:shadow-basic active:bg-white active:shadow-basic active:text-elkjop',
    primary:
      'bg-elkjop text-white hover:brightness[0.92] focus:brightness[0.92] active:brightness[0.92]',
    tertiary: 'active:text-elkjop focus:text-elkjop text-zinc-700',
    danger:
      'bg-red-600 font-semibold text-white hover:brightness-[0.9] focus:brightness-[0.9] active:brightness-[0.9] disabled:pointer-events-none disabled:bg-zinc-50 disabled:text-zinc-400 disabled:shadow-none',
  },
  threeStepIT: {
    secondary:
      'text-zinc-700 active:text-threeStepIT focus:text-threeStepIT hover:bg-white hover:shadow-basic focus:bg-white focus:text-threeStepIT focus:shadow-basic active:bg-white active:shadow-basic active:text-threeStepIT',
    primary:
      'bg-threeStepIT text-white  hover:brightness[0.95] active:brightness[0.95] focus:brightness[0.95]',
    tertiary: 'active:text-threeStepIT-text focus:text-threeStepIT-text text-zinc-700',
    danger:
      'bg-red-600 font-semibold text-white hover:brightness-[0.9] focus:brightness-[0.9] active:brightness-[0.9] disabled:pointer-events-none disabled:bg-zinc-50 disabled:text-zinc-400 disabled:shadow-none',
  },
  komplett: {
    secondary:
      'text-zinc-700 active:text-komplett focus:text-komplett hover:bg-white hover:shadow-basic focus:bg-white focus:text-komplett focus:shadow-basic active:bg-white active:shadow-basic active:text-komplett',
    primary:
      'bg-komplett hover:brightness[0.95] active:brightness[0.95] focus:brightness[0.95] text-white',
    tertiary: 'focus:text-komplett active:text-komplett text-zinc-700',
    danger:
      'bg-red-600 font-semibold text-white hover:brightness-[0.9] focus:brightness-[0.9] active:brightness-[0.9] disabled:pointer-events-none disabled:bg-zinc-50 disabled:text-zinc-400 disabled:shadow-none',
  },
}
